<script lang="ts" setup>
import { dollars } from '@nuts/auto-delivery-sdk/dist/utils/money';
import dayjs from 'dayjs';
import sanitizeHtml from 'sanitize-html';
import { computed } from 'vue';
import { useRoute } from 'vue-router';

import { getMoneyValue } from '@/lib/product/prices';
import { ApprovedReviews } from '@/lib/reviews';
import { friendlyFormat, secondsToHoursAndMinutes } from '@/utils/dateTime';
import { getPrice, NutsProduct, NutsProductVariant, PredictedShipping } from '@/utils/product';

const props = defineProps<{
  predictedShipping?: PredictedShipping;
  prefixedSku: string;
  product: NutsProduct;
  reviews: ApprovedReviews;
  variant?: NutsProductVariant;
}>();

const route = useRoute();

const htmlCleanerOptions = { allowedTags: [], allowedAttributes: {} };

const image = computed(() => props.variant?.titleImage?.small ?? props.product.titleImage?.small);

const sanitizedProductDescription = computed(() =>
  sanitizeHtml(props.product.descriptionHtml, htmlCleanerOptions),
);

const predictedShippingMessage = computed(() => {
  let message = '';
  if (props.predictedShipping?.friendly || props.predictedShipping?.date) {
    if (props.predictedShipping?.friendly) {
      if (
        props.predictedShipping?.friendly === 'today' &&
        props.predictedShipping?.expiresInSeconds
      ) {
        message += 'If you order within the next ';
        message += secondsToHoursAndMinutes(props.predictedShipping?.expiresInSeconds, true);
        message += " we'll ship today!";
      } else {
        message += `Order now and we'll ship ${props.predictedShipping.friendly}!`;
      }
    } else if (props.predictedShipping?.date) {
      message += "Order now and we'll ship ";
      message += friendlyFormat(props.predictedShipping.date);
    }
  }
  return message;
});

const price = computed(() => getPrice(props.variant ?? props.product.variantGroups[0]));
const discountedOrListPrice = computed(() => dollars(getMoneyValue(price.value)));
const priceValidUntil = computed(() => {
  const validUntil = price.value.discounted?.discount.validUntil;
  return validUntil ? dayjs(validUntil).format('YYYY-MM-DD') : '2200-01-01';
});
</script>

<template>
  <meta itemprop="brand" content="Nuts.com" />
  <meta itemprop="name" :content="product.name" />
  <meta itemprop="description" :content="sanitizedProductDescription" />
  <meta v-if="image" itemprop="image" :content="image" />
  <meta itemprop="sku" :content="prefixedSku" />
  <meta itemprop="mpn" :content="prefixedSku" />
  <meta itemprop="url" :content="`https://nuts.com${route.path}`" />
  <div v-if="variant" itemprop="offers" itemscope itemtype="http://schema.org/Offer">
    <meta itemprop="sku" :content="prefixedSku" />
    <meta itemprop="price" :content="discountedOrListPrice" />
    <meta itemprop="priceCurrency" content="USD" />
    <meta itemprop="priceValidUntil" :content="priceValidUntil" />
    <meta itemprop="itemCondition" href="http://schema.org/NewCondition" />
    <meta v-if="variant.backordered" itemprop="availability" content="OutOfStock" />
    <meta v-else itemprop="availability" content="InStock" />
    <meta itemprop="url" :content="`https://nuts.com${route.path}`" />
    <meta itemprop="deliveryLeadTime" :content="predictedShippingMessage" />
  </div>
  <div itemprop="aggregateRating" itemscope itemtype="http://schema.org/AggregateRating">
    <meta itemprop="ratingValue" :content="reviews.averageRating" />
    <meta itemprop="bestRating" content="5" />
    <meta itemprop="reviewCount" :content="reviews.totalReviews" />
  </div>
</template>
